import { TimeField } from "@/components/TimeField";
import type { SleepLogConfig } from "@/lib/schema";
import type { WidgetSettingsEditorProps } from "@/widgets/types";

type Props = WidgetSettingsEditorProps<SleepLogConfig>;

export function SettingsEditor({
  config,
  onChange,
  errors = {},
  scoringField,
}: Props) {
  const fieldError = (key: string) =>
    errors[key] ? (
      <p className="field-error" data-testid={`widget-error-${key}`}>
        {errors[key]}
      </p>
    ) : null;

  const numberPatch = (key: keyof SleepLogConfig) => (value: string) =>
    onChange({
      [key]: value === "" ? Number.NaN : Number(value),
    } as Partial<SleepLogConfig>);

  return (
    <div
      className="widget-settings"
      data-testid={`widget-settings-${config.id}`}
    >
      <div className="setting-row">
        <label className="setting-label" htmlFor={`label-${config.id}`}>
          Tittel
        </label>
        <div className="setting-control">
          <input
            id={`label-${config.id}`}
            className="input"
            type="text"
            value={config.label}
            onChange={(e) => onChange({ label: e.target.value })}
            data-testid="widget-label-input"
          />
          {fieldError("label")}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`asleep-${config.id}`}>
          Mål for å sovne
          <small>Å sovne tidligere enn dette gir full uttelling.</small>
        </label>
        <div className="setting-control">
          <TimeField
            id={`asleep-${config.id}`}
            value={config.asleepTarget}
            onChange={(next) => onChange({ asleepTarget: next ?? "" })}
            data-testid="sleep-log-asleep-target-input"
          />
          {fieldError("asleepTarget")}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`awake-${config.id}`}>
          Mål for å våkne
          <small>Å våkne tidligere enn dette gir også full uttelling.</small>
        </label>
        <div className="setting-control">
          <TimeField
            id={`awake-${config.id}`}
            value={config.awakeTarget}
            onChange={(next) => onChange({ awakeTarget: next ?? "" })}
            data-testid="sleep-log-awake-target-input"
          />
          {fieldError("awakeTarget")}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`points-${config.id}`}>
          Poeng
          <small>Full uttelling for hver av de to tidene.</small>
        </label>
        <div className="setting-control">
          <div className="num-input">
            <input
              id={`points-${config.id}`}
              type="number"
              min={0.25}
              step={0.25}
              value={Number.isFinite(config.points) ? config.points : ""}
              onChange={(e) => numberPatch("points")(e.target.value)}
              data-testid="widget-points-input"
            />
            <span className="unit">p</span>
          </div>
          {fieldError("points")}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`interval-${config.id}`}>
          Intervall
          <small>
            Hvor lenge du er for sein før neste trekk slår inn. Hvert påbegynt
            intervall koster ett trekk.
          </small>
        </label>
        <div className="setting-control">
          <div className="num-input">
            <input
              id={`interval-${config.id}`}
              type="number"
              min={1}
              step={1}
              value={
                Number.isFinite(config.intervalMinutes)
                  ? config.intervalMinutes
                  : ""
              }
              onChange={(e) => numberPatch("intervalMinutes")(e.target.value)}
              data-testid="sleep-log-interval-input"
            />
            <span className="unit">min</span>
          </div>
          {fieldError("intervalMinutes")}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`reduction-${config.id}`}>
          Trekk
          <small>Poeng som trekkes fra per påbegynte intervall.</small>
        </label>
        <div className="setting-control">
          <div className="num-input">
            <input
              id={`reduction-${config.id}`}
              type="number"
              min={0.25}
              step={0.25}
              value={
                Number.isFinite(config.reductionPerInterval)
                  ? config.reductionPerInterval
                  : ""
              }
              onChange={(e) =>
                numberPatch("reductionPerInterval")(e.target.value)
              }
              data-testid="sleep-log-reduction-input"
            />
            <span className="unit">p</span>
          </div>
          {fieldError("reductionPerInterval")}
        </div>
      </div>

      {scoringField}
    </div>
  );
}
